import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { useI18n } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";

export default function DeleteAccount() {
  const { t, lang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null);

  const expectedConfirm = t("app.account.typedConfirmValue"); // ELIMINA / DELETE

  useEffect(() => {
    // If account is already pending_deletion, /auth/me still returns for active
    // users only. So this page assumes an active user. If the user hits this
    // route while pending, they will already be signed out. No fetch needed.
    setStatus(null);
  }, []);

  const canSubmit =
    password.length >= 1 &&
    typed.trim().toUpperCase() === expectedConfirm.toUpperCase() &&
    !busy;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const r = await api.post("/account/request-deletion", {
        password,
        confirmation: typed.trim().toUpperCase(),
        reason: reason || null,
      });
      setStatus(r.data);
      toast.success(t("app.account.deleteScheduledTitle"));
      // Server clears the auth cookies. Clear FE state, then send home.
      await logout();
      navigate("/", { replace: true });
    } catch (e2) {
      const code = e2?.response?.data?.detail;
      if (code === "invalid_confirmation") setError(t("app.account.invalidConfirmation"));
      else if (code === "invalid_password") setError(t("app.account.invalidPassword"));
      else setError(extractError(e2, t("app.account.deletionError")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="delete-account-page">
      <button
        onClick={() => navigate("/app/settings")}
        className="flex items-center gap-2 text-sm mb-4"
        style={{ color: "hsl(var(--nvl-ink-soft))" }}
        data-testid="delete-account-back"
      >
        <ArrowLeft size={14} /> {t("app.nav.settings")}
      </button>

      <p className="nvl-overline" style={{ color: "hsl(var(--destructive))" }}>
        {t("app.account.deleteTitle")}
      </p>
      <h1 className="mt-3 text-4xl md:text-5xl">{t("app.account.deletePageTitle")}</h1>

      <div
        className="mt-8 nvl-card p-6 md:p-8 border-l-4"
        style={{ borderLeftColor: "hsl(var(--destructive))" }}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle size={22} style={{ color: "hsl(var(--destructive))" }} />
          <div>
            <div className="font-medium">{t("app.account.deleteWarningTitle")}</div>
            <ul className="mt-3 space-y-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.account.deleteWarningBullets").map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span className="mt-1">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-5" data-testid="delete-account-form">
          <div>
            <label className="text-xs uppercase tracking-widest"
                   style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.account.passwordConfirmLabel")}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="nvl-input mt-2"
              required
              autoComplete="current-password"
              data-testid="delete-account-password"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest"
                   style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.account.typedConfirmLabel")}
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="nvl-input mt-2"
              placeholder={expectedConfirm}
              required
              data-testid="delete-account-typed-confirm"
            />
            <div className="text-xs mt-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.account.typedConfirmHint")}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest"
                   style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.account.deleteReasonLabel")}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="nvl-input mt-2 min-h-[80px]"
              placeholder={t("app.account.deleteReasonPlaceholder")}
              data-testid="delete-account-reason"
              maxLength={500}
            />
          </div>

          {error ? (
            <div className="text-sm" style={{ color: "hsl(var(--destructive))" }}
                 data-testid="delete-account-error">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={!canSubmit}
            className="nvl-btn w-full disabled:opacity-50"
            style={{
              backgroundColor: "hsl(var(--destructive))",
              color: "white",
            }}
            data-testid="delete-account-submit"
          >
            {busy ? t("app.moderation.loading") : t("app.account.deleteSubmit")}
          </button>
        </form>
      </div>
    </div>
  );
}
